import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { Info, Loader2, Tags } from "lucide-react";
import type { Account } from "@shared/schema";
import { apiRequest, getApiPath } from "@/lib/queryClient";
import { useAdminBasePath } from "@/lib/adminPath";
import {
  CUSTOMER_TYPES_PATH,
  customerTypePath,
  isAllowedByAccountMenu,
  isHiddenByType,
  type CustomerTypeDetail,
  type CustomerTypeSummary,
  type TypedProduct,
} from "@/lib/customerTypes";
import { useToast } from "@/hooks/use-toast";
import { parseApiError } from "@/components/pin-setup-code-step";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// The account page's view of customer types (BRIEF-customer-types-zones-picksheet.md
// §2a "Account page"): one panel on the Menu tab, beside the switches it works with.
// In an org with no customer types it doesn't render, so the page is exactly what it
// was before types existed.

type AccountWithType = Pick<Account, "id" | "name" | "isGroupParent" | "customerTypeId">;

const DEFAULT_VALUE = "default";

function useCustomerTypes() {
  return useQuery<CustomerTypeSummary[]>({ queryKey: [getApiPath(CUSTOMER_TYPES_PATH)] });
}

/** The account's type with its rules, for "what does it hide". */
export function useAccountCustomerType(account: AccountWithType | undefined) {
  const typeId = account?.customerTypeId ?? null;
  const { data: type } = useQuery<CustomerTypeDetail>({
    queryKey: [getApiPath(customerTypePath(typeId ?? 0))],
    enabled: typeId !== null,
  });
  return typeId !== null && type?.id === typeId ? type : null;
}

/**
 * Menu tab, above the switches: which customer type this account is, and how much
 * that type hides. The switches below mirror the type (its rows locked) and stay a
 * per-account override on top — they can hide more, never bring back what the type
 * hides (the server ANDs the two), and never change or clear the type.
 */
export function AccountMenuCustomerType({
  account,
  type,
  products,
  customMenu,
}: {
  account: AccountWithType;
  type: CustomerTypeDetail | null;
  products: (TypedProduct & { isActive: boolean })[];
  /** The switches as they stand on screen, saved or not. */
  customMenu: Parameters<typeof isAllowedByAccountMenu>[1];
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const adminBase = useAdminBasePath();
  const { data: types, isError, refetch, isRefetching } = useCustomerTypes();

  const assign = useMutation({
    mutationFn: async (customerTypeId: number | null) => {
      await apiRequest("PUT", `/api/accounts/${account.id}/customer-type`, { customerTypeId });
      return customerTypeId;
    },
    onSuccess: (customerTypeId) => {
      // Prefix match: the account list and this account's own ["/api/accounts", id] key.
      queryClient.invalidateQueries({ queryKey: ["/api/accounts"] });
      queryClient.invalidateQueries({ queryKey: [getApiPath(CUSTOMER_TYPES_PATH)] });
      const name = types?.find((t) => t.id === customerTypeId)?.name;
      toast({
        title: name ? `${account.name} is now ${name}` : `${account.name} is back on the default`,
        description: name
          ? `They see the ${name} menu and prices the next time their menu loads.`
          : "They see the master menu at its prices the next time their menu loads.",
      });
    },
    onError: (error) => {
      toast({
        title: "Couldn't set the customer type",
        description: parseApiError(error).message ?? "Please try again.",
        variant: "destructive",
      });
    },
  });

  if (isError && account.customerTypeId != null) {
    // The account has a type: say the list failed rather than hide the picker.
    return (
      <div className="flex items-center justify-between gap-4 rounded-md border p-3" data-testid="panel-account-customer-type">
        <p className="text-sm text-red-600">Couldn't load the customer types.</p>
        <Button type="button" variant="outline" size="sm" disabled={isRefetching} onClick={() => refetch()}>
          {isRefetching && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Try again
        </Button>
      </div>
    );
  }
  // No types in this org (or still loading): nothing to choose — the tab stays as it was.
  if (!types || (types.length === 0 && account.customerTypeId == null)) return null;

  const current = account.customerTypeId == null ? DEFAULT_VALUE : String(account.customerTypeId);
  const active = products.filter((p) => p.isActive);
  const hidden = type ? active.filter((p) => isHiddenByType(p, type)).length : 0;
  const extra = type ? active.filter((p) => !isHiddenByType(p, type) && !isAllowedByAccountMenu(p, customMenu)).length : 0;

  return (
    <div className="space-y-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900" data-testid="panel-account-customer-type">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Label htmlFor="account-customer-type" className="flex items-center gap-2 text-blue-900">
          <Tags className="h-4 w-4 text-blue-600" />
          Customer type
        </Label>
        {account.isGroupParent ? null : (
          <div className="flex items-center gap-2">
            {assign.isPending && <Loader2 className="h-4 w-4 animate-spin text-blue-400" />}
            <Select
              value={current}
              disabled={assign.isPending}
              onValueChange={(value) => {
                if (value === current) return;
                assign.mutate(value === DEFAULT_VALUE ? null : Number(value));
              }}
            >
              <SelectTrigger id="account-customer-type" className="w-full bg-white sm:w-64" data-testid="select-account-customer-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={DEFAULT_VALUE}>Default (master menu)</SelectItem>
                {types.map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      {account.isGroupParent ? (
        <p className="text-blue-800" data-testid="text-customer-type-group-parent">
          Orders are placed by this group's locations, so each location has its own customer type. Set it on each
          location's Menu tab.
        </p>
      ) : type ? (
        <p className="text-blue-800">
          {type.name} hides{" "}
          <span className="font-semibold" data-testid="text-account-type-hidden-count">{hidden}</span> of {active.length} active products
          {extra > 0 ? (
            <>
              , plus <span className="font-semibold" data-testid="text-account-extra-hidden-count">{extra}</span> hidden for this
              customer only
            </>
          ) : null}
          . You can hide more items &amp; categories for just this account below.{" "}
          <Link href={`${adminBase}/settings`} className="underline">
            Edit {type.name} in Settings
          </Link>
        </p>
      ) : (
        <p className="text-blue-800">The master menu at its prices. Pick a type to give this customer that type's menu and prices.</p>
      )}
    </div>
  );
}
